import { API_KEY_ACCESS_LEVEL, API_KEY_ACCESS_LEVEL_NONE } from './apiKeyAccess.js';

/**
 * The one permission vocabulary for API keys (ADR
 * docs/plans/active/public-api/project-public-api/2026-10-15-api-key-permissions/adr/2026-10-15-key-permissions-and-live-ceiling.md).
 * Keys hold permissions from the roles catalog (main.permission); a request may
 * use only what the key holds and what its creator may do right now.
 */

export { API_KEY_ACCESS_LEVEL_NONE };

export const API_KEY_UNIVERSAL_PERMISSIONS = Object.freeze(['universal:read', 'universal:create', 'universal:update', 'universal:delete']);

// The seven view permissions: the default for a new key, trimmed to what its
// creator holds.
export const API_KEY_VIEW_PERMISSIONS = Object.freeze(['workflow:view', 'log:view', 'credential:view', 'member:view', 'company:view', 'subscription:view', 'embedded:view']);

// What a key can be given, grouped by area for the key management UI, in
// display order. Every permission any /v2 endpoint, embedded MCP tool, or the
// company MCP endpoint (/mcp) requires is here, and nothing else.
export const API_KEY_PERMISSION_GROUPS = Object.freeze([
  Object.freeze({
    key: 'workflows',
    label: 'Workflows',
    permissions: Object.freeze([Object.freeze({ key: 'workflow:view', label: 'View workflows' }), Object.freeze({ key: 'workflow:create', label: 'Create workflows' }), Object.freeze({ key: 'workflow:edit', label: 'Edit workflows' }), Object.freeze({ key: 'workflow:delete', label: 'Delete workflows' }), Object.freeze({ key: 'workflow:run', label: 'Run and cancel workflows' })])
  }),
  Object.freeze({ key: 'runs', label: 'Runs', permissions: Object.freeze([Object.freeze({ key: 'log:view', label: 'View runs and logs' })]) }),
  Object.freeze({
    key: 'connections',
    label: 'Connections',
    permissions: Object.freeze([Object.freeze({ key: 'credential:view', label: 'View connections' }), Object.freeze({ key: 'credential:edit', label: 'Test connections' }), Object.freeze({ key: 'credential:delete', label: 'Delete connections' })])
  }),
  Object.freeze({
    key: 'universal',
    label: 'Universal API',
    permissions: Object.freeze([Object.freeze({ key: 'universal:read', label: 'Run read actions and lookups' }), Object.freeze({ key: 'universal:create', label: 'Run create actions' }), Object.freeze({ key: 'universal:update', label: 'Run update actions' }), Object.freeze({ key: 'universal:delete', label: 'Run delete actions' })])
  }),
  Object.freeze({
    key: 'members',
    label: 'Members and roles',
    permissions: Object.freeze([Object.freeze({ key: 'member:view', label: 'View members and roles' }), Object.freeze({ key: 'member:create', label: 'Invite members' }), Object.freeze({ key: 'member:edit', label: 'Change member roles' }), Object.freeze({ key: 'member:delete', label: 'Remove members' }), Object.freeze({ key: 'member:owner_edit', label: 'Invite, change, or remove Owners' }), Object.freeze({ key: 'role:manage', label: 'Create, edit, and delete roles' })])
  }),
  Object.freeze({
    key: 'company',
    label: 'Company',
    permissions: Object.freeze([Object.freeze({ key: 'company:view', label: 'View company and environments' }), Object.freeze({ key: 'company:edit', label: 'Edit company profile' }), Object.freeze({ key: 'subscription:view', label: 'View usage' })])
  }),
  Object.freeze({ key: 'apiKeys', label: 'API keys', permissions: Object.freeze([Object.freeze({ key: 'api_key:manage', label: 'List, create, and revoke API keys' })]) }),
  Object.freeze({
    key: 'embedded',
    label: 'Embedded',
    permissions: Object.freeze([Object.freeze({ key: 'embedded:view', label: 'View end-user connections' }), Object.freeze({ key: 'embedded:create', label: 'Create connect links' })])
  }),
  Object.freeze({
    key: 'childCompanies',
    label: 'Child organizations',
    permissions: Object.freeze([Object.freeze({ key: 'child_company:access', label: 'Act in child organizations' }), Object.freeze({ key: 'child_company:manage', label: 'Create and release child organizations' })])
  })
]);

// The 27 keys any endpoint, tool, or /mcp may require.
export const API_KEY_PERMISSIONS = Object.freeze(API_KEY_PERMISSION_GROUPS.flatMap((group) => group.permissions.map((permission) => permission.key)));

// What each legacy level means as a set: the backfill (migration
// 20261015_02, whose VALUES a test holds to this map) and a create request
// that still sends accessLevel.
const READ_ONLY_GRANTS = Object.freeze([...API_KEY_VIEW_PERMISSIONS, 'child_company:access']);

export const API_KEY_LEVEL_GRANTS = Object.freeze({
  [API_KEY_ACCESS_LEVEL.READ_ONLY]: READ_ONLY_GRANTS,
  [API_KEY_ACCESS_LEVEL.RUN_WORKFLOWS]: Object.freeze([...READ_ONLY_GRANTS, 'workflow:run', ...API_KEY_UNIVERSAL_PERMISSIONS]),
  [API_KEY_ACCESS_LEVEL.FULL_ACCESS]: API_KEY_PERMISSIONS
});

// Fixed thresholds, highest first: a set derives to the first level whose
// threshold it contains. Never "every permission", so adding a permission
// later never silently demotes a key.
const FULL_ACCESS_EXCLUDED = new Set(['child_company:access', 'child_company:manage', 'company:edit', 'member:owner_edit']);

export const API_KEY_LEVEL_THRESHOLDS = Object.freeze([Object.freeze({ level: API_KEY_ACCESS_LEVEL.FULL_ACCESS, permissions: Object.freeze(API_KEY_PERMISSIONS.filter((key) => !FULL_ACCESS_EXCLUDED.has(key))) }), Object.freeze({ level: API_KEY_ACCESS_LEVEL.RUN_WORKFLOWS, permissions: Object.freeze([...API_KEY_VIEW_PERMISSIONS, 'workflow:run', ...API_KEY_UNIVERSAL_PERMISSIONS]) }), Object.freeze({ level: API_KEY_ACCESS_LEVEL.READ_ONLY, permissions: API_KEY_VIEW_PERMISSIONS })]);

// A Universal action's declared operation, and the permission running it needs.
export const UNIVERSAL_OPERATION_PERMISSION = Object.freeze({
  read: 'universal:read',
  create: 'universal:create',
  update: 'universal:update',
  delete: 'universal:delete'
});

// What a v2 endpoint declares when it checks its permissions in the handler,
// after the resource that decides them resolves (Run Universal Action).
export const API_KEY_PERMISSIONS_IN_HANDLER = 'inHandler';
